import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Typography, Empty } from 'antd';
import { transformToSankeyData, getSankeyStats } from '../../utils/sankeyTransform';

const { Text } = Typography;

// Color palette for distinct city colors
const cityColors = [
  '#5470c6', // Blue
  '#91cc75', // Green
  '#fac858', // Yellow
  '#ee6666', // Red
  '#73c0de', // Light Blue
  '#3ba272', // Teal
  '#fc8452', // Orange
  '#9a60b4', // Purple
  '#ea7ccc', // Pink
  '#48b8d0', // Cyan
  '#6b8e23', // Olive
  '#cd5c5c', // Indian Red
  '#4682b4', // Steel Blue
  '#daa520', // Goldenrod
  '#8fbc8f', // Dark Sea Green
  '#bc8f8f', // Rosy Brown
  '#6495ed', // Cornflower Blue
  '#f4a460', // Sandy Brown
  '#9370db', // Medium Purple
  '#20b2aa', // Light Sea Green
];

// Get consistent color for a city name
const getCityColor = (cityName, cityColorMap) => {
  const baseName = cityName.trim();
  if (!cityColorMap.has(baseName)) {
    const colorIndex = cityColorMap.size % cityColors.length;
    cityColorMap.set(baseName, cityColors[colorIndex]);
  }
  return cityColorMap.get(baseName);
};

const SankeyDiagram = ({ trips, filters = {} }) => {
  const sankeyData = useMemo(() => {
    if (!trips || trips.length === 0) {
      return { nodes: [], links: [], totalTrips: 0, filteredCount: 0 };
    }
    return transformToSankeyData(trips, filters);
  }, [trips, filters]);

  const stats = useMemo(() => getSankeyStats(sankeyData), [sankeyData]);

  const option = useMemo(() => {
    if (sankeyData.nodes.length === 0) {
      return null;
    }

    // Create a color map for consistent city colors
    const cityColorMap = new Map();

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#fff',
        borderWidth: 1,
        textStyle: {
          color: '#1a4c3a',
        },
        formatter: (params) => {
          if (params.dataType === 'edge') {
            return `
              <div style="padding: 4px;">
                <strong>${params.data.source.trim()}</strong> → <strong>${params.data.target.trim()}</strong>
                <br/>
                Viaggi: <span style="color: #1890ff; font-weight: bold;">${params.data.value.toLocaleString()}</span>
              </div>
            `;
          }
          return `<strong>${params.name.trim()}</strong>`;
        },
      },
      series: [
        {
          type: 'sankey',
          layout: 'none',
          emphasis: {
            focus: 'adjacency',
          },
          nodeAlign: 'left',
          orient: 'horizontal',
          draggable: true,
          data: sankeyData.nodes.map((node) => ({
            name: node.name,
            itemStyle: {
              color: getCityColor(node.name, cityColorMap),
              borderWidth: 1,
            },
          })),
          links: sankeyData.links,
          lineStyle: {
            color: 'gradient',
            curveness: 0.5,
            opacity: 0.5,
          },
          label: {
            show: true,
            position: 'right',
            color: '#1a4c3a',
            fontSize: 11,
            formatter: (params) => params.name.trim(),
          },
          left: 50,
          right: 150,
          top: 20,
          bottom: 20,
        },
      ],
    };
  }, [sankeyData]);

  if (!trips || trips.length === 0) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty
          description={
            <Text style={{ color: '#1a4c3a' }}>
              Nessun dato disponibile. Seleziona un tipo di giorno e una fascia oraria.
            </Text>
          }
        />
      </div>
    );
  }

  if (sankeyData.nodes.length === 0) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty
          description={
            <Text style={{ color: '#1a4c3a' }}>
              Nessun flusso trovato con i filtri correnti. Prova ad abbassare il limite minimo.
            </Text>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Stats bar */}
      <div
        style={{
          padding: '12px 16px',
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
        }}
      >
        <Text style={{ color: '#1a4c3a' }}>
          <strong style={{ color: '#1a4c3a' }}>{stats.linkCount}</strong> flussi
        </Text>
        <Text style={{ color: '#1a4c3a' }}>
          <strong style={{ color: '#1a4c3a' }}>{stats.totalTrips.toLocaleString()}</strong> viaggi
          totali
        </Text>
        {stats.topFlow && (
          <Text style={{ color: '#1a4c3a' }}>
            Top: <strong>{stats.topFlow.from}</strong> → <strong>{stats.topFlow.to}</strong> (
            {stats.topFlow.value.toLocaleString()})
          </Text>
        )}
      </div>

      {/* Chart */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <ReactECharts
          option={option}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      </div>
    </div>
  );
};

export default SankeyDiagram;
